// useIntelFacetCounts — server-side per-signal_type facet totals for the
// hierarchical filter rail.
//
// Why a dedicated hook instead of counting the loaded signals: the map feed
// (`useIntelFeed`) pulls with `has_geom: true, limit: 200`. OFAC sanctions
// (SAN, ~950k rows) are entity-keyed with NULL geometry, so they never reach
// the client sample — counting that sample makes the rail show "Sanctions 0"
// despite a million records. This hits POST /v1/intel/aggregations with the
// `signal_type` dimension, which counts every filtered row regardless of
// geometry, so the rail's domain/subcategory totals are the true filtered
// counts.
//
// Shape matches `bucketCounts`'s buckets ({ value, count }[]) so the rail's
// existing code→subcategory/domain projection consumes it unchanged.

import { API_BASE_URL } from '@/lib/env';
import { readAccessToken } from '@/state/auth';
import type { filters as f } from '@eidollon/contracts';
import { useQuery } from '@tanstack/react-query';

export interface FacetBucket {
  value: string;
  count: number;
}

const EMPTY: FacetBucket[] = [];

export function useIntelSignalTypeFacet(state: f.FilterState): FacetBucket[] {
  const token = readAccessToken() ?? '';

  // A facet must NOT filter by its own dimension, or selecting one type zeros
  // every other row's count (you could never add/switch types). Strip
  // `signal_type` from the state used for THIS facet's counts; keep every other
  // active filter (region/source/severity/time) so the type counts still
  // reflect the rest of the query.
  const facetState: f.FilterState = { ...state };
  // biome-ignore lint/performance/noDelete: one-off shallow copy, not a hot loop.
  delete (facetState as Record<string, unknown>)['signal_type'];

  const query = useQuery({
    queryKey: ['intel-facet-signal-type', facetState, token],
    queryFn: async (): Promise<FacetBucket[]> => {
      const res = await fetch(`${API_BASE_URL}/v1/intel/aggregations`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ state: facetState, group_by: ['signal_type'] }),
      });
      if (!res.ok) throw new Error(`intel facets ${res.status}`);
      const json = (await res.json()) as {
        buckets?: { signal_type?: { signal_type: string; count: number }[] };
      };
      const rows = json.buckets?.signal_type ?? [];
      return rows.map((r) => ({ value: r.signal_type, count: r.count }));
    },
    // 60s so toggling a facet on/off/on within the window is a cache hit.
    staleTime: 60_000,
  });

  return query.data ?? EMPTY;
}
